import { Bell, User, Building2, LogOut, Activity, Sparkles, Trash2, Crown, ShieldCheck, CreditCard, Gift, XCircle, FlaskConical, Home, Landmark, Menu, X } from "lucide-react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePremium } from "@/context/PremiumContext";
import { openBillingPortal } from "@/lib/checkout";
import { CancelSaveModal } from "@/components/CancelSaveModal";
import { BackgroundSettings } from "@/components/BackgroundSettings";
import { Palette } from "lucide-react";
import ProfileModal from "@/components/ProfileModal";
import NotificationBell from "@/components/NotificationBell";
import { NAV_TABS } from "@/lib/navTabs";

export default function Header({ mode, setMode, onOpenDispatch, dispatchCount, onSeed, onReset, overdueCount = 0 }) {
  const { user, logout } = useAuth();
  const { openUpgrade } = usePremium();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [bgOpen, setBgOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();
  const activeKey = loc.pathname.startsWith("/dashboard") ? mode
    : loc.pathname === "/" ? "briefing"
    : loc.pathname.startsWith("/snapbudget") ? "snapbudget"
    : loc.pathname.startsWith("/tax-hub") ? "tax" : "";
  const onTab = (key) => {
    if (key === "personal") setMode("personal");
    else if (key === "business") setMode("business");
    else navigate(key === "briefing" ? "/" : key === "snapbudget" ? "/snapbudget" : "/tax-hub");
  };

  return (
    <>
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F9F7]/85 border-b border-[var(--line)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <button data-testid="mobile-nav-toggle" onClick={() => setNavOpen(true)} className="lg:hidden flex items-center justify-center w-9 h-9 border border-[var(--line)] bg-white text-[var(--text-2)]"><Menu className="w-5 h-5" /></button>
          <button data-testid="brand-home" onClick={() => navigate("/")} title="Morning Briefing" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-[var(--accent)] flex items-center justify-center transition-colors">
              <Bell className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <span className="hidden sm:block font-mono text-sm tracking-[0.18em] uppercase group-hover:text-[var(--accent)] transition-colors">RemindMe &amp; Do</span>
          </button>
        </div>

        <nav className="hidden lg:flex items-center gap-2" data-testid="main-tabs">
          {NAV_TABS.map((t) => {
            const on = activeKey === t.key;
            return (
              <button
                key={t.key}
                data-testid={`nav-tab-${t.key}`}
                onClick={() => onTab(t.key)}
                title={t.label}
                className={`relative inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${on ? t.active : t.idle}`}
              >
                <t.Icon className="w-4 h-4" /> {t.label}
                {t.key === "personal" && overdueCount > 0 && (
                  <span data-testid="overdue-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF6B6B] text-white text-[10px] font-bold flex items-center justify-center">{overdueCount}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <NotificationBell />
          {user?.beta_active ? (
            <span data-testid="beta-badge" className="hidden sm:flex items-center gap-1.5 h-9 px-3 border border-[var(--accent)] text-[var(--accent)] text-sm font-medium">
              <FlaskConical className="w-4 h-4" /> Beta{user?.beta_days_left != null ? ` · ${user.beta_days_left}d` : ""}
            </span>
          ) : user?.premium_active ? (
            <span data-testid="premium-badge" className="hidden sm:flex items-center gap-1.5 h-9 px-3 border border-[var(--accent)] text-[var(--accent)] text-sm font-medium">
              <Crown className="w-4 h-4" /> {user?.platinum_active ? "Platinum" : "Premium"}
              {user?.platinum_active && <span data-testid="priority-badge" className="ml-1.5 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[var(--accent)] text-white px-1.5 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> Priority</span>}
            </span>
          ) : (
            <button
              data-testid="upgrade-btn"
              onClick={openUpgrade}
              className="hidden sm:flex items-center gap-1.5 h-9 px-3 bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Crown className="w-4 h-4" /> Upgrade
            </button>
          )}
          <button
            data-testid="open-background"
            onClick={() => setBgOpen(true)}
            title="Customize background"
            className="flex items-center justify-center w-9 h-9 border border-[var(--line)] bg-white hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <Palette className="w-4 h-4" />
          </button>
          <button
            data-testid="open-dispatch"
            onClick={onOpenDispatch}
            className="relative flex items-center gap-2 h-9 px-3 border border-[var(--line)] bg-white hover:border-[var(--accent)] transition-colors text-sm"
          >
            <Activity className="w-4 h-4" /> <span className="hidden md:inline">Dispatch Log</span>
            {dispatchCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-5 h-5 px-1 bg-[var(--accent)] text-white text-[10px] font-mono flex items-center justify-center">
                {dispatchCount}
              </span>
            )}
          </button>

          <div className="relative">
            <button
              data-testid="user-menu"
              onClick={() => setMenu((m) => !m)}
              className="w-9 h-9 bg-[#1A1A18] text-white flex items-center justify-center hover:bg-[var(--accent)] transition-colors"
            >
              <span className="font-mono text-xs">{(user?.name || "U").slice(0, 1).toUpperCase()}</span>
            </button>
            {menu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                <div className="absolute right-0 mt-2 w-60 bg-white border border-[var(--line)] shadow-xl z-50" data-testid="user-dropdown">
                  <div className="p-4 border-b border-[var(--line)]">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-[var(--text-2)] truncate font-mono">{user?.email}</p>
                    {user?.premium_active && (
                      <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[var(--accent)] flex items-center gap-1">
                        <Crown className="w-3 h-3" /> {user?.platinum_active ? "Platinum · Priority support" : "Premium active"}
                      </p>
                    )}
                  </div>
                  <button data-testid="profile-btn" onClick={() => { setProfileOpen(true); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <User className="w-4 h-4 text-[var(--text-2)]" /> My contact
                  </button>
                  <button data-testid="pricing-btn" onClick={() => { navigate("/pricing"); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <Crown className="w-4 h-4 text-[var(--accent)]" /> View pricing
                  </button>
                  <button data-testid="tax-hub-menu-btn" onClick={() => { navigate("/tax-hub"); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors lg:hidden">
                    <Landmark className="w-4 h-4 text-[var(--accent)]" /> Tax Clarity Hub
                  </button>
                  {user?.has_billing && (
                    <button data-testid="billing-btn" onClick={() => { openBillingPortal().catch(() => {}); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                      <CreditCard className="w-4 h-4 text-[var(--text-2)]" /> Manage billing
                    </button>
                  )}
                  <button data-testid="support-btn" onClick={() => { navigate("/support"); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <span className="w-4 h-4 grid place-items-center text-[var(--text-2)] font-bold">?</span> Support &amp; contact
                  </button>
                  <button data-testid="refer-btn" onClick={() => { navigate("/refer"); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <Gift className="w-4 h-4 text-[var(--accent)]" /> Refer a friend
                  </button>
                  {user?.has_subscription && (
                    <button data-testid="cancel-plan-btn" onClick={() => { setCancelOpen(true); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                      <XCircle className="w-4 h-4 text-[var(--text-2)]" /> Cancel plan
                    </button>
                  )}
                  {user?.role === "admin" && (
                    <button data-testid="admin-portal-btn" onClick={() => { navigate("/admin"); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                      <ShieldCheck className="w-4 h-4 text-[var(--business)]" /> Admin portal
                    </button>
                  )}
                  <button data-testid="seed-demo" onClick={() => { onSeed(); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <Sparkles className="w-4 h-4 text-[var(--accent)]" /> Load demo data
                  </button>
                  <button data-testid="reset-demo" onClick={() => { onReset(); setMenu(false); }} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors">
                    <Trash2 className="w-4 h-4 text-[var(--danger)]" /> Clear all my data
                  </button>
                  <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-4 h-11 text-sm hover:bg-[#f9f9f7] transition-colors border-t border-[var(--line)]">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-2 -mt-1">
        <p data-testid="app-tagline" className="text-center text-xs italic text-[var(--text-2)]">
          &ldquo;Your daily life command center. One login. Your day, your business, your money — in under 10 minutes.&rdquo;
        </p>
      </div>
    </header>
      {navOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 max-w-[82%] bg-[#F9F9F7] border-r border-[var(--line)] p-4 flex flex-col gap-2 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-sm tracking-[0.18em] uppercase">Menu</span>
              <button data-testid="mobile-nav-close" onClick={() => setNavOpen(false)} className="w-9 h-9 grid place-items-center border border-[var(--line)] bg-white"><X className="w-5 h-5" /></button>
            </div>
            <button data-testid="mnav-briefing" onClick={() => { navigate("/"); setNavOpen(false); }} className="flex items-center gap-3 h-14 px-4 border border-[var(--line)] bg-white text-base hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Home className="w-5 h-5" /> Briefing</button>
            <button data-testid="mnav-personal" onClick={() => { setMode("personal"); navigate("/dashboard"); setNavOpen(false); }} className={`flex items-center gap-3 h-14 px-4 border text-base transition-colors ${mode === "personal" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--line)] bg-white"}`}><User className="w-5 h-5" /> Personal{overdueCount > 0 && <span className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-[#FF6B6B] text-white text-[11px] font-bold grid place-items-center">{overdueCount}</span>}</button>
            <button data-testid="mnav-business" onClick={() => { setMode("business"); navigate("/dashboard"); setNavOpen(false); }} className={`flex items-center gap-3 h-14 px-4 border text-base transition-colors ${mode === "business" ? "border-[var(--business)] text-[var(--business)]" : "border-[var(--line)] bg-white"}`}><Building2 className="w-5 h-5" /> Business</button>
            <button data-testid="mnav-snapbudget" onClick={() => { navigate("/snapbudget"); setNavOpen(false); }} className="flex items-center gap-3 h-14 px-4 rounded-lg border border-emerald-500/30 bg-[#0d1730] text-base text-emerald-300 hover:border-emerald-400 transition-colors"><img src="/snapbudget-logo.webp" alt="" className="w-6 h-6 rounded-full" /> SnapBudget</button>
            <button data-testid="mnav-tax" onClick={() => { navigate("/tax-hub"); setNavOpen(false); }} className="flex items-center gap-3 h-14 px-4 border border-[var(--line)] bg-white text-base hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Landmark className="w-5 h-5" /> Tax Clarity Hub</button>
          </div>
        </div>
      )}
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
      <CancelSaveModal open={cancelOpen} onClose={() => setCancelOpen(false)} />
      <BackgroundSettings open={bgOpen} onClose={() => setBgOpen(false)} scope={mode} />
    </>
  );
}
